import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { IconCheck } from '../../../../../icons/check';
import { ChartDataEnum } from '../../../../../../../defs/api';
import { cn } from '../../../../../../../utils/tailwind';
import { Tooltip } from '../../../../../tooltip/tooltip';

export interface InlineLegendItemProps {
    label: string;
    color: string;
    chartDataType: ChartDataEnum | null;
    onChange?: () => void;
    checked?: boolean;
    displayLabel?: string;
    tooltip?: React.ReactNode;
    disabled?: boolean;
    variant?: 'dropdown' | 'inline';
}

export const InlineLegendItem: React.FC<InlineLegendItemProps> = observer(
    ({
        label,
        color,
        chartDataType,
        onChange,
        checked = true,
        displayLabel,
        tooltip,
        disabled = false,
        variant = 'dropdown',
    }: InlineLegendItemProps) => {
        const { t } = useTranslation();

        const text =
            displayLabel ??
            (chartDataType === ChartDataEnum.PinnedPortfolioValue ? label : t(`pages.index.chart.legend.${label}`));

        return (
            <label
                className={cn(
                    'flex cursor-pointer items-center justify-between text-text-muted hover:text-brand-secondary',
                    variant === 'dropdown' && 'border-t border-border py-[1.2rem] first:border-t-0',
                    variant === 'inline' &&
                        'min-h-[3.2rem] rounded-full border border-border px-[1.2rem] py-[0.6rem] transition-opacity',
                    variant === 'inline' && !checked && 'opacity-50',
                    disabled && 'cursor-not-allowed hover:text-text-muted',
                )}
            >
                <div className="flex items-center whitespace-nowrap font-medium">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                        className="absolute -left-[9999px]"
                    />
                    <div
                        className={cn(
                            'mr-[0.8rem] flex items-center justify-center rounded-full',
                            variant === 'dropdown' && 'h-[2rem] w-[2rem]',
                            variant === 'inline' && 'h-[1.8rem] w-[1.8rem]',
                        )}
                        style={{ backgroundColor: color }}
                    >
                        <span
                            className={
                                checked
                                    ? '[&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:min-w-[1.2rem] [&_i]:text-neutral-white [&_i]:opacity-100'
                                    : '[&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:min-w-[1.2rem] [&_i]:text-neutral-white [&_i]:opacity-0'
                            }
                        >
                            <IconCheck />
                        </span>
                    </div>
                    <span>{text}</span>
                    {tooltip ? (
                        <span className="ml-[0.6rem] inline-flex text-text-muted [&_i]:h-[1.4rem] [&_i]:w-[1.4rem]">
                            <Tooltip content={tooltip} icon size="medium">
                                <span className="sr-only">{text} help</span>
                            </Tooltip>
                        </span>
                    ) : null}
                </div>
            </label>
        );
    },
);
